import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Wheat, Plus, MapPin } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const CROPS = ['wheat', 'barley', 'oats', 'canola', 'pulses', 'cotton', 'sugar_cane', 'rice', 'sorghum'];
const EMPTY = { name: '', lat: '', lng: '', crop_type: '', planted_area_ha: '', expected_yield_t_ha: '', insured_price_per_t: '' };

export const CropSeasonPanel = ({ api, policy }) => {
  const [view, setView] = useState(null);
  const [rows, setRows] = useState([{ ...EMPTY }]);
  const [busy, setBusy] = useState(false);
  const [fitDate, setFitDate] = useState('');

  const load = useCallback(() => api.get(`/crop/${policy.policy_id}/season`)
    .then(r => setView(r.data)).catch(() => {}), [api, policy.policy_id]);
  useEffect(() => { load(); }, [load]);

  const setRow = (i, field, v) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: v } : r));

  const declare = async () => {
    const situations = rows.filter(r => r.name && r.planted_area_ha).map(r => ({
      name: r.name, lat: Number(r.lat), lng: Number(r.lng),
      crop_type: r.crop_type || null, planted_area_ha: Number(r.planted_area_ha),
      expected_yield_t_ha: r.expected_yield_t_ha ? Number(r.expected_yield_t_ha) : null,
      insured_price_per_t: r.insured_price_per_t ? Number(r.insured_price_per_t) : null,
    }));
    if (!situations.length) { toast.error('Add at least one paddock with a name and planted area'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/crop/${policy.policy_id}/planting-declaration`, { situations });
      const adj = r.data.adjustment;
      toast.success(`Planting declared — ${r.data.declared_area_ha} ha, premium $${r.data.declared_premium.toLocaleString()} (${adj > 0 ? `+$${adj.toLocaleString()} additional` : adj < 0 ? `$${Math.abs(adj).toLocaleString()} return` : 'no adjustment'})`);
      setRows([{ ...EMPTY }]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Declaration failed'); }
    finally { setBusy(false); }
  };

  const closeSeason = async () => {
    try { await api.post(`/crop/${policy.policy_id}/close-season`); toast.success('Season closed at harvest'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Close failed'); }
  };

  const s = view?.season || {};
  const latest = view?.latest_declaration;
  const ht = view?.harvest_timeliness;

  const recordFit = async () => {
    if (!fitDate) { toast.error('Pick the fit-to-harvest date'); return; }
    try {
      const r = await api.post(`/crop/${policy.policy_id}/fit-to-harvest`, { fit_to_harvest_date: fitDate });
      toast.success(`Harvest timeliness clock started — cover ceases ${r.data.harvest_timeliness.cover_ceases_on}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <Card className="p-4 rounded-md space-y-3" style={{ border: '1px solid rgba(29,158,117,0.25)' }} data-testid="crop-season-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-2">
          <Wheat className="w-4 h-4 mt-0.5" style={{ color: C.green }} />
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Crop Season — Planting Declaration</h3>
            <p className="text-[11px]" style={{ color: C.slate }}>{s.attachment_note}</p>
          </div>
        </div>
        <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase" data-testid="crop-season-status"
          style={{ background: s.status === 'declared' ? 'rgba(29,158,117,0.1)' : s.status === 'harvested' ? 'rgba(136,150,168,0.12)' : 'rgba(232,160,32,0.12)', color: s.status === 'declared' ? C.green : s.status === 'harvested' ? C.slate : '#A8720E' }}>
          {s.status || 'estimated'}
        </span>
      </div>

      <div className="flex flex-wrap gap-4 text-[11px]" style={{ color: C.slate }}>
        <span>Planting opens <strong style={{ color: C.navy }}>{s.planting_window_start || '—'}</strong></span>
        <span>Harvest closes <strong style={{ color: C.navy }}>{s.harvest_window_end || '—'}</strong></span>
        <span>Estimated <strong style={{ color: C.navy }}>{view?.estimated?.area_ha || '—'} ha {view?.estimated?.primary_crop || ''}</strong></span>
        {latest && <span>Declared <strong style={{ color: C.green }}>{latest.declared_area_ha} ha / ${latest.declared_premium?.toLocaleString()}</strong>
          {latest.adjustment !== 0 && <em style={{ color: latest.adjustment > 0 ? '#A8720E' : C.green }}> ({latest.adjustment > 0 ? '+' : ''}${latest.adjustment?.toLocaleString()} {latest.adjustment_kind?.replace('_', ' ')})</em>}</span>}
      </div>

      {ht && (
        <div className="p-2.5 rounded-sm flex flex-wrap items-center gap-3 text-[11px]" data-testid="crop-harvest-timeliness"
          style={{ background: ht.cover_ceased ? 'rgba(226,75,74,0.07)' : 'rgba(232,160,32,0.08)', border: `1px solid ${ht.cover_ceased ? 'rgba(226,75,74,0.25)' : 'rgba(232,160,32,0.2)'}` }}>
          <span className="font-semibold uppercase tracking-wider text-[10px]" style={{ color: ht.cover_ceased ? C.red : '#A8720E' }}>
            CR-E-005 Harvest Timeliness
          </span>
          {ht.fit_to_harvest_date ? (
            <span style={{ color: C.navy }}>
              Fit to harvest <strong>{ht.fit_to_harvest_date}</strong> · cover ceases <strong data-testid="crop-cover-ceases-on">{ht.cover_ceases_on}</strong> ({ht.days} days)
              {ht.cover_ceased && <strong style={{ color: C.red }}> — COVER HAS CEASED, harvested or not</strong>}
            </span>
          ) : (
            <span className="flex items-center gap-1.5" style={{ color: C.slate }}>
              Cover ceases {ht.days} days after fit-to-harvest — record it:
              <input type="date" value={fitDate} onChange={e => setFitDate(e.target.value)} data-testid="crop-fit-date-input"
                className="px-2 py-0.5 border border-input rounded-sm text-[11px] bg-white" />
              <button onClick={recordFit} data-testid="crop-fit-submit-btn"
                className="px-2 py-0.5 rounded text-[10px] font-medium text-white" style={{ background: C.navy }}>Start clock</button>
            </span>
          )}
        </div>
      )}

      {s.status !== 'harvested' && (
        <div className="p-3 rounded-sm space-y-2" style={{ background: 'rgba(29,158,117,0.05)' }} data-testid="crop-declare-form">
          <p className="text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1" style={{ color: C.slate }}>
            <MapPin className="w-3 h-3" /> Situations (paddocks) — geolocation required per paddock
          </p>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-7 gap-1.5">
              <input value={r.name} onChange={e => setRow(i, 'name', e.target.value)} placeholder="Paddock name" data-testid={`crop-sit-name-${i}`} className="px-2 py-1 border border-input rounded-sm text-xs bg-white" />
              <input value={r.lat} onChange={e => setRow(i, 'lat', e.target.value)} placeholder="Lat (-31.95)" type="number" step="any" data-testid={`crop-sit-lat-${i}`} className="px-2 py-1 border border-input rounded-sm text-xs bg-white" />
              <input value={r.lng} onChange={e => setRow(i, 'lng', e.target.value)} placeholder="Lng (148.6)" type="number" step="any" data-testid={`crop-sit-lng-${i}`} className="px-2 py-1 border border-input rounded-sm text-xs bg-white" />
              <select value={r.crop_type} onChange={e => setRow(i, 'crop_type', e.target.value)} data-testid={`crop-sit-crop-${i}`} className="px-2 py-1 border border-input rounded-sm text-xs bg-white">
                <option value="">Crop — policy default</option>
                {CROPS.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
              </select>
              <input value={r.planted_area_ha} onChange={e => setRow(i, 'planted_area_ha', e.target.value)} placeholder="Area (ha)" type="number" data-testid={`crop-sit-area-${i}`} className="px-2 py-1 border border-input rounded-sm text-xs bg-white" />
              <input value={r.expected_yield_t_ha} onChange={e => setRow(i, 'expected_yield_t_ha', e.target.value)} placeholder="Yield t/ha" type="number" step="any" className="px-2 py-1 border border-input rounded-sm text-xs bg-white" />
              <input value={r.insured_price_per_t} onChange={e => setRow(i, 'insured_price_per_t', e.target.value)} placeholder="$/tonne" type="number" className="px-2 py-1 border border-input rounded-sm text-xs bg-white" />
            </div>
          ))}
          <div className="flex gap-2">
            <button onClick={() => setRows(p => [...p, { ...EMPTY }])} data-testid="crop-add-situation-btn"
              className="flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] font-medium" style={{ background: 'rgba(29,158,117,0.1)', color: C.green }}>
              <Plus className="w-3 h-3" /> Paddock
            </button>
            <button onClick={declare} disabled={busy} data-testid="crop-declare-submit-btn"
              className="px-4 py-1 rounded-sm text-xs font-medium text-white disabled:opacity-40" style={{ background: C.navy }}>
              {busy ? 'Rating…' : 'Declare Planting & Adjust Premium'}
            </button>
            {s.status === 'declared' && (
              <button onClick={closeSeason} data-testid="crop-close-season-btn"
                className="px-3 py-1 rounded-sm text-xs font-medium ml-auto" style={{ background: 'rgba(136,150,168,0.12)', color: C.slate }}>
                Close Season (Harvest)
              </button>
            )}
          </div>
        </div>
      )}

      {latest?.situations?.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left" style={{ color: C.slate }}>
              <th className="py-1 pr-2 font-medium">Paddock</th><th className="py-1 pr-2 font-medium">Location</th>
              <th className="py-1 pr-2 font-medium">Crop</th><th className="py-1 pr-2 font-medium">Area</th>
              <th className="py-1 pr-2 font-medium">Value</th><th className="py-1 font-medium">Premium</th>
            </tr></thead>
            <tbody>
              {latest.situations.map((sit, i) => (
                <tr key={i} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`crop-declared-row-${i}`}>
                  <td className="py-1 pr-2" style={{ color: C.navy }}>{sit.name}</td>
                  <td className="py-1 pr-2 font-mono text-[10px]" style={{ color: C.slate }}>{sit.lat}, {sit.lng}</td>
                  <td className="py-1 pr-2 capitalize">{(sit.crop_type || '').replace('_', ' ')}</td>
                  <td className="py-1 pr-2">{sit.planted_area_ha} ha</td>
                  <td className="py-1 pr-2 font-mono">${(sit.insured_value || 0).toLocaleString()}</td>
                  <td className="py-1 font-mono" style={{ color: C.navy }}>${(sit.premium || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default CropSeasonPanel;
